import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBook,
  FaPlus,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTimes,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  formatShortDate,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import { nameMatchesSearch } from "../../../utility/bowlerName";
import {
  createAverageCatalog,
  listAverageCatalogs,
} from "../../../utility/averageCatalogs";

const chipClass = (active) =>
  `max-lg:min-h-11 max-lg:inline-flex max-lg:items-center px-3 py-1.5 text-sm rounded-lg font-display font-bold transition-colors ${
    active
      ? "bg-brand-charcoal text-brand-cream"
      : "bg-white text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-charcoal"
  }`;

const SortableTh = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  const Icon = !active ? FaSort : sortOrder === "asc" ? FaSortUp : FaSortDown;
  return (
    <th className="px-4 py-2 text-left text-xs font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal">
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1.5 hover:text-brand-gold"
        aria-sort={
          active ? (sortOrder === "asc" ? "ascending" : "descending") : "none"
        }
      >
        {label}
        <Icon
          className={`h-3 w-3 ${active ? "text-brand-gold" : "opacity-50"}`}
        />
      </button>
    </th>
  );
};

const compareNullableDate = (left, right, direction) => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  if (left === right) return 0;
  return (left < right ? -1 : 1) * direction;
};

const compareCatalogs = (left, right, sortBy, sortOrder) => {
  const direction = sortOrder === "desc" ? -1 : 1;
  if (sortBy === "asOfDate") {
    const compared = compareNullableDate(
      left.asOfDate,
      right.asOfDate,
      direction
    );
    if (compared !== 0) return compared;
  }
  const compared = (left.name || "").localeCompare(right.name || "", undefined, {
    sensitivity: "base",
  });
  if (compared !== 0) {
    return sortBy === "name" ? compared * direction : compared;
  }
  return compareNullableDate(left.createdAt, right.createdAt, -1);
};

const catalogMatchesSearch = (catalog, query) => {
  if (nameMatchesSearch(catalog.name, query)) return true;
  const tokens = (query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = [
    catalog.asOfDate,
    catalog.asOfDate ? formatShortDate(catalog.asOfDate) : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

const AverageCatalogs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { canManageTournaments } = usePermissions();
  const organizationID = currentOrganization?.id;

  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [asOfDate, setAsOfDate] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("asOfDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");

  const loadCatalogs = useCallback(async () => {
    if (!organizationID) {
      setCatalogs([]);
      setLoading(false);
      return;
    }
    setError("");
    try {
      const rows = await listAverageCatalogs(organizationID);
      setCatalogs(rows);
    } catch (err) {
      console.error(err);
      setError("Could not load average catalogs.");
    } finally {
      setLoading(false);
    }
  }, [organizationID]);

  useEffect(() => {
    setLoading(true);
    loadCatalogs();
  }, [loadCatalogs]);

  const visibleCatalogs = useMemo(
    () =>
      catalogs
        .filter((catalog) => catalogMatchesSearch(catalog, search))
        .sort((left, right) => compareCatalogs(left, right, sortBy, sortOrder)),
    [catalogs, search, sortBy, sortOrder]
  );

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortOrder(field === "asOfDate" ? "desc" : "asc");
  };

  const resetCreateForm = () => {
    setCreating(false);
    setName("");
    setAsOfDate("");
    setActionError("");
  };

  const handleCreate = async () => {
    if (!user || !organizationID) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setActionError("Enter a catalog name.");
      return;
    }
    setActionError("");
    try {
      const catalog = await createAverageCatalog({
        organizationID,
        name: trimmed,
        asOfDate,
        createdBy: user.id,
      });
      resetCreateForm();
      navigate(`/organization/average-catalogs/${catalog.id}`);
    } catch (err) {
      console.error(err);
      setActionError("Could not create the catalog. Try again.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6" role="status" aria-label="Loading average catalogs">
        <SkeletonBoard rows={2} />
        <SkeletonBoard header={false} rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-brand-gold mb-1">
              Beta
            </p>
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Average catalogs
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              A catalog is a named book of league averages. Upload sheets here.
              Matching names and writing tournament averages are not in this
              screen yet.
            </p>
          </div>
          {canManageTournaments && !creating ? (
            <PrimaryButton onClick={() => setCreating(true)} icon={FaPlus}>
              Create catalog
            </PrimaryButton>
          ) : null}
        </div>
      </header>

      {error ? (
        <InlineAlert type="error" title="There was a problem" message={error} />
      ) : null}
      {actionError ? (
        <InlineAlert
          type="error"
          title="There was a problem"
          message={actionError}
          onClose={() => setActionError("")}
        />
      ) : null}

      {creating && canManageTournaments ? (
        <div className="card-board p-6">
          <h2 className="heading-display text-lg mb-4">New average catalog</h2>
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="January 1, 2026 averages"
            className="w-full max-w-md px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink mb-4"
          />
          <label className="block text-sm font-medium text-brand-charcoal mb-1">
            As of date
          </label>
          <input
            type="date"
            value={asOfDate}
            onChange={(event) => setAsOfDate(event.target.value)}
            className="w-full max-w-md px-3 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink mb-1"
          />
          <p className="text-sm text-brand-muted mb-4">
            Optional. Use a new catalog when the cutoff date changes.
          </p>
          <div className="flex gap-2">
            <PrimaryButton onClick={handleCreate}>Create</PrimaryButton>
            <SecondaryButton
              onClick={resetCreateForm}
              className="!px-3 !py-2 text-sm"
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      ) : null}

      {catalogs.length === 0 && !creating ? (
        <div className="card-board p-8 text-center">
          <FaBook className="mx-auto h-10 w-10 text-brand-muted mb-3" />
          <h2 className="heading-display text-lg mb-2">No average catalogs yet</h2>
          <p className="text-brand-muted mb-4">
            Create a catalog, then upload league sheets into it.
          </p>
          {canManageTournaments ? (
            <PrimaryButton onClick={() => setCreating(true)} icon={FaPlus}>
              Create catalog
            </PrimaryButton>
          ) : null}
        </div>
      ) : catalogs.length ? (
        <div className="card-board overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-brand-charcoal/10 space-y-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-brand-mist" />
              </div>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name or date"
                className="block w-full pl-10 pr-9 py-2 border border-brand-charcoal/20 rounded-lg bg-white placeholder-brand-mist text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink sm:text-sm"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-brand-mist hover:text-brand-charcoal"
                  title="Clear search"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 md:hidden">
              <span className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted">
                Sort
              </span>
              {[
                { id: "name", label: "Name" },
                { id: "asOfDate", label: "As of" },
              ].map((field) => (
                <button
                  key={field.id}
                  type="button"
                  onClick={() => handleSortChange(field.id)}
                  className={chipClass(sortBy === field.id)}
                >
                  {field.label}
                </button>
              ))}
            </div>
          </div>
          {visibleCatalogs.length ? (
            <div className="max-w-full overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead>
                  <tr>
                    <SortableTh
                      label="Name"
                      field="name"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSortChange}
                    />
                    <SortableTh
                      label="As of"
                      field="asOfDate"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSortChange}
                    />
                  </tr>
                </thead>
                <tbody>
                  {visibleCatalogs.map((catalog) => (
                    <tr
                      key={catalog.id}
                      className="border-t border-brand-charcoal/10"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/organization/average-catalogs/${catalog.id}`}
                          className="font-medium text-brand-charcoal hover:underline"
                        >
                          {catalog.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-brand-muted">
                        {catalog.asOfDate
                          ? formatShortDate(catalog.asOfDate)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-6 py-6 text-sm text-brand-muted">
              No catalogs match that search.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AverageCatalogs;
